import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandInboundPurchasingItemGroup } from "services/sosInventoryService/expandItemGroup";
import { afterTouchLine } from "services/sosInventoryService/purchasingTransaction/afterTouchLine";
import { setPageDirty } from "services/utility/edit";

import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";
import { usePrivileges } from "hooks/usePrivileges";
import { useRelated } from "hooks/useRelated";

import { MemoizedLineItem } from "views/PurchaseTransactions/MemoizedLineItem";

import { USER_PRIVILEGE } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    objectType,
    record,
    lines,
    taxCodes,
    items,
    totals,
    itemsCount,
    addItem,
    classes,
    classesCount,
    lineHandler,
    lineItemsErrors,
    setClearLines,
    bins,
    location,
    showAppliedTo,
    showBinSerialAndLot,
    showReceived,
  } = props;

  const customers = useRelated("customer");
  const jobWorkcenters = useJobWorkcenters();
  const { hasPrivilegesOrIsAdmin } = usePrivileges();
  const viewCosts = hasPrivilegesOrIsAdmin(USER_PRIVILEGE.viewCosts);

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  // SETTINGS
  const showVolumeOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnPurchases
  );
  const showWeightOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnPurchases
  );
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const showCustomerOnPurchaseForms = useSelector(
    (state) => state.userCompanySettings.settings.showCustomerOnPurchaseForms
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const globalEnabled = useSelector(
    (state) => state.userCompanySettings.settings.globalEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  async function expandItemGroupWrapper(lineToExpand) {
    lineHandler({ type: "delete", deleteAt: lineToExpand.lineNumber });
    const lines = await expandInboundPurchasingItemGroup(
      lineToExpand,
      objectType,
      record
    );
    const afterTouchedLines = lines.map((line) => afterTouchLine(line, "item"));
    setPageDirty();
    lineHandler({
      type: "insertMany",
      insertAt: lineToExpand.lineNumber,
      lines: afterTouchedLines,
    });
  }

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <EditLineButtonHeading recordId={record.id} />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            <LineColumnHeading labelCode="PartNumber" />
            <LineColumnHeading labelCode="Quantity" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            {showWeightOnPurchases && <LineColumnHeading labelCode="Weight" />}
            {showVolumeOnPurchases && <LineColumnHeading labelCode="Volume" />}
            {viewCosts && (
              <LineColumnHeading labelCode="UnitPrice" align="right" />
            )}
            {viewCosts && (
              <LineColumnHeading labelCode="Amount" align="right" />
            )}
            {showReceived && (
              <LineColumnHeading labelCode="Received" align="right" />
            )}
            {showAppliedTo && <LineColumnHeading labelCode="AppliedTo" />}
            {classEnabled && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {globalEnabled && (
              <LineColumnFillHeading
                labelCode="Tax"
                field="taxCode"
                lineHandler={lineHandler}
              />
            )}
            {showCustomerOnPurchaseForms && (
              <LineColumnFillHeading
                labelCode="Customer"
                field="customer"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && (
              <LineColumnFillHeading
                labelCode="Job"
                field="jobWorkcenter"
                lineHandler={lineHandler}
              />
            )}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={handleDrop}>
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => {
                  const lineReadOnly = readOnlyLineIds.has(index);

                  return (
                    <MemoizedLineItem
                      key={line.id || `temp-${index}`}
                      line={line}
                      index={index}
                      objectType={objectType}
                      record={record}
                      lineHandler={lineHandler}
                      expandItemGroup={expandItemGroupWrapper}
                      items={items}
                      itemsCount={itemsCount}
                      addItem={addItem}
                      taxCodes={taxCodes}
                      classes={classes}
                      classesCount={classesCount}
                      customers={customers}
                      jobWorkcenters={jobWorkcenters}
                      bins={bins}
                      location={location}
                      lineItemsErrors={lineItemsErrors}
                      showAppliedTo={showAppliedTo}
                      showBinSerialAndLot={showBinSerialAndLot}
                      showReceived={showReceived}
                      insertEmptyLine={insertEmptyLine}
                      isLastLine={index === lines.length - 1}
                      setLineEdit={setLineEdit}
                      lineReadOnly={lineReadOnly}
                      nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                      handleDeleteLine={handleDeleteLine}
                    />
                  );
                })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          showWeight={showWeightOnPurchases}
          showVolume={showVolumeOnPurchases}
          totals={totals}
          firstCellSpan={5}
          lineHandler={lineHandler}
          objectType={objectType}
          recordId={record.id}
        />
      </Table>
    </TableContainer>
  );
}
